import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE from '../config';

const getItemLabels = (countryName) => {
  if (countryName === 'Bulgaria') return {
    computer: 'Computer / Компютър',
    monitor1: 'Monitor 1',
    monitor2: 'Monitor 2',
    token: 'Security Token / Токен',
    other: 'Other / Друго'
  };
  if (countryName === 'Poland') return {
    computer: 'Computer / Komputer',
    monitor1: 'Monitor 1',
    monitor2: 'Monitor 2',
    token: 'Security Token / Token bezpieczeństwa',
    other: 'Other / Inne'
  };
  return {
    computer: 'Computer',
    monitor1: 'Monitor 1',
    monitor2: 'Monitor 2',
    token: 'Security Token',
    other: 'Other'
  };
};

// ── Bilingual text helpers ────────────────────────────────────────────────────

const T = {
  Bulgaria: {
    title:    'Equipment Receipt Confirmation / Потвърждение за получаване',
    subtitle: 'Please review your equipment and confirm receipt / Моля, прегледайте оборудването и потвърдете получаването',
    labels: {
      Employee:        'Employee / Служител',
      'Employee ID':   'Employee ID / Служебен №',
      Department:      'Department / Отдел',
      'Assignment Type': 'Assignment Type / Вид',
      'Receipt Method':  'Receipt Method / Начин на получаване',
      'Delivery Address': 'Delivery Address / Адрес на доставка',
      'Date Issued':   'Date Issued / Дата на издаване',
      'New Equipment': 'New Equipment / Ново оборудване',
      'Equipment Change': 'Equipment Change / Смяна на оборудване',
      'Self-pickup':   'Self-pickup / Лично получаване',
      Delivery:        'Delivery / Доставка',
    },
    equipTitle: 'Equipment Assigned / Разпределено оборудване',
    checkboxText: [
      'I confirm that I have received the equipment listed above in good condition.',
      'Потвърждавам, че съм получил/а описаното оборудване в добро техническо и естетично състояние.'
    ],
    checkboxText2: [
      'I understand that I am responsible for its safekeeping and return upon request.',
      'Разбирам, че нося лична отговорност за неговото опазване и съм длъжен/а да го върна при поискване.'
    ],
    signBtn:     'Confirm Receipt & Sign / Потвърждавам и подписвам',
    legalNote: [
      'By clicking the button above, your IP address, browser information, and the current timestamp will be recorded as legally defensible proof of receipt under eIDAS Regulation (EU) 910/2014.',
      'С натискането на бутона Вашият IP адрес, информация за браузъра и текущото дата и час ще бъдат записани като правно-валидно доказателство за получаване съгласно Регламент eIDAS (ЕС) 910/2014.'
    ],
    signed:      'Signature Recorded / Подписът е записан',
    signedThank: ['Thank you, ', '! Your receipt has been confirmed.', 'Благодарим ви, ', '! Получаването е официално потвърдено.'],
    signedNote:  'Your IP address has been recorded. You may close this window. / Вашият IP адрес беше записан. Можете да затворите прозореца.',
    alreadySigned: 'Already Signed / Вече подписан',
    alreadySignedNote: 'This document has already been signed. / Този документ вече е подписан.',
    expired:     'Link Expired / Препратката е изтекла',
    expiredNote: ['This signature link has expired (7 days). Please contact your IT administrator.',
                  'Тази препратка за подпис е изтекла (7 дни). Свържете се с вашия IT администратор.'],
  },
  Poland: {
    title:    'Equipment Receipt Confirmation / Potwierdzenie odbioru sprzętu',
    subtitle: 'Please review your equipment and confirm receipt / Prosimy przejrzeć sprzęt i potwierdzić odbiór',
    labels: {
      Employee:        'Employee / Pracownik',
      'Employee ID':   'Employee ID / Nr pracownika',
      Department:      'Department / Dział',
      'Assignment Type': 'Assignment Type / Typ',
      'Receipt Method':  'Receipt Method / Sposób odbioru',
      'Delivery Address': 'Delivery Address / Adres dostawy',
      'Date Issued':   'Date Issued / Data wystawienia',
      'New Equipment': 'New Equipment / Nowy sprzęt',
      'Equipment Change': 'Equipment Change / Zmiana sprzętu',
      'Self-pickup':   'Self-pickup / Odbiór osobisty',
      Delivery:        'Delivery / Dostawa',
    },
    equipTitle: 'Equipment Assigned / Przydzielony sprzęt',
    checkboxText: [
      'I confirm that I have received the equipment listed above in good condition.',
      'Potwierdzam, że otrzymałem/am wymieniony powyżej sprzęt w dobrym stanie technicznym i estetycznym.'
    ],
    checkboxText2: [
      'I understand that I am responsible for its safekeeping and return upon request.',
      'Rozumiem, że jestem odpowiedzialny/a za jego przechowywanie i zobowiązuję się zwrócić go na żądanie.'
    ],
    signBtn:     'Confirm Receipt & Sign / Potwierdzam odbiór i podpisuję',
    legalNote: [
      'By clicking the button above, your IP address, browser information, and the current timestamp will be recorded as legally defensible proof of receipt under eIDAS Regulation (EU) 910/2014.',
      'Klikając przycisk, Twój adres IP, informacje o przeglądarce i aktualny znacznik czasu zostaną zapisane jako prawnie wiążący dowód odbioru zgodnie z Rozporządzeniem eIDAS (UE) 910/2014.'
    ],
    signed:      'Signature Recorded / Podpis zapisany',
    signedThank: ['Thank you, ', '! Your receipt has been confirmed.', 'Dziękujemy, ', '! Odbiór został oficjalnie potwierdzony.'],
    signedNote:  'Your IP address has been recorded. You may close this window. / Twój adres IP został zapisany. Możesz zamknąć to okno.',
    alreadySigned: 'Already Signed / Już podpisano',
    alreadySignedNote: 'This document has already been signed. / Dokument został już podpisany.',
    expired:     'Link Expired / Link wygasł',
    expiredNote: ['This signature link has expired (7 days). Please contact your IT administrator.',
                  'Ten link do podpisu wygasł (7 dni). Skontaktuj się z administratorem IT.'],
  }
};

// Returns text object for the given country (falls back to English-only)
const useTranslations = (countryName) => {
  const key = countryName && T[countryName] ? countryName : null;
  return key ? T[key] : null;
};

// Renders a label with translation if available
const tLabel = (t, key) => (t && t.labels[key]) ? t.labels[key] : key;

export default function SignDocument() {
  const { token } = useParams();

  const [status, setStatus] = useState('loading'); // loading | ready | already_signed | expired | error | signed
  const [assignment, setAssignment] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [signing, setSigning] = useState(false);
  const [signedAt, setSignedAt] = useState('');
  const [checked, setChecked] = useState(false);
  const [typedName, setTypedName] = useState('');

  // Translation object — resolved once assignment is loaded
  const t = useTranslations(assignment?.countryName);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const res = await fetch(`${API_BASE}/sign/${token}`);
        const data = await res.json();

        if (res.status === 404 || res.status === 400) {
          if (data.signedAt) {
            setStatus('already_signed');
            setSignedAt(data.signedAt);
          } else if (data.message?.includes('expired')) {
            setStatus('expired');
          } else {
            setStatus('error');
            setErrorMsg(data.message || 'Invalid link.');
          }
          return;
        }

        if (!res.ok) {
          setStatus('error');
          setErrorMsg(data.message || 'Failed to load document.');
          return;
        }

        setAssignment(data.data);
        setStatus('ready');
      } catch (err) {
        setStatus('error');
        setErrorMsg('Network error. Please try again.');
      }
    };

    fetchAssignment();
  }, [token]);

  const nameMatch = typedName.trim().toLowerCase() === (assignment?.employeeName || '').trim().toLowerCase();

  const handleSign = async () => {
    if (!checked || !nameMatch) return;
    setSigning(true);
    try {
      const res = await fetch(`${API_BASE}/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typedName: typedName.trim() })
      });
      const data = await res.json();

      if (res.ok) {
        setSignedAt(data.signedAt);
        setStatus('signed');
      } else {
        setErrorMsg(data.message || 'Failed to record signature.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    } finally {
      setSigning(false);
    }
  };

  // ── Renders ────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={{ color: '#64748b', marginTop: 16 }}>Loading document…</p>
        </div>
      </div>
    );
  }

  if (status === 'already_signed') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconCircle('#16a34a')}>✔</div>
          <h2 style={{ color: '#15803d', marginBottom: 8 }}>
            {t ? t.alreadySigned : 'Already Signed'}
          </h2>
          <p style={{ color: '#555' }}>
            {t ? t.alreadySignedNote : 'This document has already been signed.'}
          </p>
          {signedAt && (
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 28 }}>
              Signed on {new Date(signedAt).toLocaleString('en-GB')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconCircle('#d97706')}>⚠</div>
          <h2 style={{ color: '#b45309', marginBottom: 8 }}>
            {t ? t.expired : 'Link Expired'}
          </h2>
          {t ? (
            <>
              <p style={{ color: '#555' }}>{t.expiredNote[0]}</p>
              <p style={{ color: '#888', fontSize: '0.88rem', marginBottom: 28 }}>{t.expiredNote[1]}</p>
            </>
          ) : (
            <>
              <p style={{ color: '#555' }}>This signature link has expired (links are valid for 7 days).</p>
              <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 28 }}>
                Please contact your IT administrator to request a new link.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (status === 'signed') {
    const name = assignment?.employeeName;
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconCircle('#2563eb')}>✍</div>
          <h2 style={{ color: '#1d4ed8', marginBottom: 8 }}>
            {t ? t.signed : 'Signature Recorded'}
          </h2>
          {t ? (
            <>
              <p style={{ color: '#374151' }}>
                {t.signedThank[0]}<strong>{name}</strong>{t.signedThank[1]}
              </p>
              <p style={{ color: '#555' }}>
                {t.signedThank[2]}<strong>{name}</strong>{t.signedThank[3]}
              </p>
            </>
          ) : (
            <p style={{ color: '#374151' }}>
              Thank you, <strong>{name}</strong>! Your receipt has been confirmed.
            </p>
          )}
          {signedAt && (
            <p style={{ ...styles.metaBox, margin: '16px 28px 8px' }}>
              ⏱ Signed at {new Date(signedAt).toLocaleString('en-GB')}
            </p>
          )}
          <p style={{ color: '#9ca3af', fontSize: '0.78rem', padding: '8px 28px 28px' }}>
            {t ? t.signedNote : 'Your IP address has been recorded. You may close this window.'}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconCircle('#dc2626')}>✕</div>
          <h2 style={{ color: '#b91c1c', marginBottom: 8 }}>Invalid Link</h2>
          <p style={{ color: '#555' }}>{errorMsg}</p>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 28 }}>
            Please contact your IT administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  // ── Ready to sign ──────────────────────────────────────────────────────
  const ITEM_LABELS = getItemLabels(assignment.countryName);
  const items = Object.entries(ITEM_LABELS)
    .filter(([key]) => assignment[key])
    .map(([key, label]) => ({ label, value: assignment[key] }));

  const assignTypeEN = assignment.equipmentType === 'new' ? 'New Equipment' : 'Equipment Change';
  const receiptMethodEN = assignment.receiveMethod === 'self_pickup' ? 'Self-pickup' : 'Delivery';

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, maxWidth: 580 }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ fontSize: '1.8rem' }}>✍️</div>
          <h1 style={styles.headerTitle}>
            {t ? t.title : 'Equipment Receipt Confirmation'}
          </h1>
          <p style={styles.headerSub}>
            {t ? t.subtitle : 'Please review your equipment and confirm receipt'}
          </p>
        </div>

        {/* Employee info */}
        <div style={styles.section}>
          <Row label={tLabel(t, 'Employee')} value={assignment.employeeName} />
          <Row label={tLabel(t, 'Employee ID')} value={assignment.employeeId} />
          {assignment.departmentName && (
            <Row label={tLabel(t, 'Department')} value={assignment.departmentName} />
          )}
          <Row
            label={tLabel(t, 'Assignment Type')}
            value={t
              ? (assignment.equipmentType === 'new' ? tLabel(t, 'New Equipment') : tLabel(t, 'Equipment Change'))
              : assignTypeEN}
          />
          <Row
            label={tLabel(t, 'Receipt Method')}
            value={t
              ? (assignment.receiveMethod === 'self_pickup' ? tLabel(t, 'Self-pickup') : tLabel(t, 'Delivery'))
              : receiptMethodEN}
          />
          {assignment.address && (
            <Row label={tLabel(t, 'Delivery Address')} value={assignment.address} />
          )}
          <Row label={tLabel(t, 'Date Issued')} value={new Date(assignment.createdAt).toLocaleDateString('en-GB')} />
        </div>

        {/* Equipment list */}
        <div style={styles.equipSection}>
          <h3 style={styles.equipTitle}>
            {t ? t.equipTitle : 'Equipment Assigned'}
          </h3>
          {items.length === 0 ? (
            <p style={{ color: '#6b7280', fontStyle: 'italic' }}>See attached protocol for details.</p>
          ) : (
            <table style={styles.equipTable}>
              <tbody>
                {items.map(({ label, value }) => (
                  <tr key={label}>
                    <td style={styles.equipLabel}>{label}</td>
                    <td style={styles.equipValue}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Confirmation checkbox */}
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{ marginRight: 10, marginTop: 3, width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
          />
          <span>
            {t ? (
              <>
                {t.checkboxText[0]}
                <br /><span style={{ color: '#6b7280' }}>{t.checkboxText[1]}</span>
                <br /><br />
                {t.checkboxText2[0]}
                <br /><span style={{ color: '#6b7280' }}>{t.checkboxText2[1]}</span>
              </>
            ) : (
              'I confirm that I have received the equipment listed above in good condition. ' +
              'I understand that I am responsible for its safekeeping and return upon request.'
            )}
          </span>
        </label>

        {/* Name confirmation input */}
        <div style={{ padding: '8px 28px 4px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
            Type your full name to confirm / Въведете пълното си име за потвърждение:
          </p>
          <p style={{ margin: '0 0 8px', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
            {assignment.employeeName}
          </p>
          <input
            type="text"
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
            placeholder="Type your full name…"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `2px solid ${typedName.length === 0 ? '#e2e8f0' : nameMatch ? '#22c55e' : '#f87171'}`,
              borderRadius: 6,
              fontSize: '0.95rem',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }}
          />
          {typedName.length > 0 && !nameMatch && (
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#ef4444' }}>
              Name does not match / Името не съвпада
            </p>
          )}
        </div>

        {/* Sign button */}
        <button
          style={{
            ...styles.signBtn,
            opacity: checked && nameMatch ? 1 : 0.5,
            cursor: checked && nameMatch ? 'pointer' : 'not-allowed'
          }}
          onClick={handleSign}
          disabled={!checked || !nameMatch || signing}
        >
          {signing ? 'Recording Signature…' : `✔ ${t ? t.signBtn : 'Confirm Receipt & Sign'}`}
        </button>

        {/* Legal note */}
        {t ? (
          <p style={styles.legalNote}>
            {t.legalNote[0]}
            <br /><br />
            <span style={{ color: '#b0b8c8' }}>{t.legalNote[1]}</span>
          </p>
        ) : (
          <p style={styles.legalNote}>
            By clicking the button above, your IP address, browser information, and the current
            timestamp will be recorded as legally defensible proof of receipt under eIDAS Regulation
            (EU) 910/2014 for simple electronic signatures.
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ minWidth: 180, color: '#64748b', fontSize: '0.88rem', flexShrink: 0, paddingRight: 8 }}>{label}</span>
      <span style={{ color: '#1e293b', fontWeight: 500, fontSize: '0.88rem' }}>{value}</span>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 16px'
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: 560,
    overflow: 'hidden',
    textAlign: 'center'
  },
  header: {
    background: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
    padding: '28px 32px',
    color: '#fff'
  },
  headerTitle: {
    margin: '8px 0 4px',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#fff'
  },
  headerSub: {
    margin: 0,
    color: '#93c5fd',
    fontSize: '0.9rem'
  },
  section: {
    padding: '20px 28px',
    textAlign: 'left'
  },
  equipSection: {
    background: '#f8fafc',
    margin: '0 28px',
    borderRadius: 8,
    padding: '14px 18px',
    textAlign: 'left',
    border: '1px solid #e2e8f0'
  },
  equipTitle: {
    margin: '0 0 10px',
    color: '#1e293b',
    fontSize: '0.95rem',
    fontWeight: 600
  },
  equipTable: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  equipLabel: {
    padding: '5px 12px 5px 0',
    color: '#64748b',
    fontSize: '0.88rem',
    width: 130
  },
  equipValue: {
    padding: '5px 0',
    color: '#1e293b',
    fontWeight: 500,
    fontSize: '0.88rem'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '20px 28px 8px',
    textAlign: 'left',
    fontSize: '0.88rem',
    color: '#374151',
    cursor: 'pointer'
  },
  signBtn: {
    display: 'block',
    width: 'calc(100% - 56px)',
    margin: '12px 28px',
    padding: '14px',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '1rem',
    fontWeight: 700,
    transition: 'opacity 0.2s'
  },
  legalNote: {
    padding: '8px 28px 24px',
    fontSize: '0.72rem',
    color: '#94a3b8',
    lineHeight: 1.5,
    textAlign: 'left'
  },
  metaBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 6,
    padding: '10px 16px',
    color: '#15803d',
    fontSize: '0.9rem'
  },
  iconCircle: (color) => ({
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: color,
    color: '#fff',
    fontSize: '1.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '28px auto 16px'
  }),
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '28px auto 0'
  }
};
